import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import ReactPaginate from "react-paginate";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import ConfigDB from "../../config";
import Utils from "../../utils";
import {
  getSupplierList,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  setDefaultSupplier,
} from "../../services/SuppliersTPServices";

// ============================================================
// SuppliersTP — fornecedores TP-internos (public.suppliers).
// Distintos do módulo Moloni Suppliers (m_supplier).
// Usado pelos lotes para identificar de onde vem o stock.
// ============================================================

const SuppliersTP = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const RolePermission = Utils.getRecordForCurrentPath(location);
  const tableLengthList = ConfigDB.data.dataTableLength;
  const data = localStorage.getItem(ConfigDB.data.login_data);
  const userData = JSON.parse(data) || {};

  const [params, setParams] = useState({
    limit: tableLengthList[0],
    page: 1,
    search: "",
  });
  const [list, setList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [modifyModal, setModifyModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    id: "",
    name: "",
    nif: "",
    tua_number: "",
    address: "",
    notes: "",
    is_default: 0,
  });
  const [errors, setErrors] = useState({});

  const [delModal, setDelModal] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [isDelLoad, setIsDelLoad] = useState(false);

  const fetchList = () => {
    getSupplierList(params)
      .then((res) => {
        setList(res.data?.data || []);
        setTotalRecords(res.data?.pagination?.total || 0);
      })
      .catch((err) => toast.error(Utils.getErrorMessage(err)));
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page, params.limit, params.search]);

  const handleParamChange = (e, type) => {
    const next = { ...params };
    if (type === "length") next.limit = parseInt(e.target.value);
    else if (type === "search") {
      next.page = 1;
      next.search = e.target.value;
    } else if (type === "pagination") next.page = e.selected + 1;
    setParams(next);
  };

  const openAdd = () => {
    setForm({ id: "", name: "", nif: "", tua_number: "", address: "", notes: "", is_default: 0 });
    setErrors({});
    setModifyModal(true);
  };
  const openEdit = (s) => {
    setForm({
      id: s.id,
      name: s.name || "",
      nif: s.nif || "",
      tua_number: s.tua_number || "",
      address: s.address || "",
      notes: s.notes || "",
      is_default: s.is_default || 0,
    });
    setErrors({});
    setModifyModal(true);
  };

  const submit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name?.trim()) errs.name = "required";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setIsSaving(true);
    const payload = {
      name: form.name.trim(),
      nif: form.nif || null,
      tua_number: form.tua_number || null,
      address: form.address || null,
      notes: form.notes || null,
      is_default: form.is_default ? 1 : 0,
    };
    if (form.id) payload.id = form.id;
    const promise = form.id ? updateSupplier(payload) : createSupplier(payload);
    promise
      .then((res) => {
        toast.dismiss();
        toast.success(res.data?.message || t("Saved"));
        setIsSaving(false);
        setModifyModal(false);
        fetchList();
      })
      .catch((err) => {
        setIsSaving(false);
        toast.error(Utils.getErrorMessage(err));
      });
  };

  const confirmDelete = () => {
    if (!delTarget?.id) return;
    setIsDelLoad(true);
    deleteSupplier({ id: delTarget.id })
      .then((res) => {
        toast.success(res.data?.message || t("Deleted"));
        setIsDelLoad(false);
        setDelModal(false);
        fetchList();
      })
      .catch((err) => {
        setIsDelLoad(false);
        toast.error(Utils.getErrorMessage(err));
      });
  };

  const setDefault = (s) => {
    setDefaultSupplier({ id: s.id })
      .then(() => fetchList())
      .catch((err) => toast.error(Utils.getErrorMessage(err)));
  };

  return (
    <div className="page-body">
      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-md-12 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h4 className="mb-0">{t("suppliers")}</h4>
            {RolePermission?.add === 1 && (
              <Button color="primary" onClick={openAdd}>+ {t("add_supplier")}</Button>
            )}
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-4">
            <input
              className="form-control"
              placeholder={t("search")}
              value={params.search}
              onChange={(e) => handleParamChange(e, "search")}
            />
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0">
            <table className="table table-hover mb-0 align-middle">
              <thead>
                <tr>
                  <th>{t("name")}</th>
                  <th>{t("nif") || "NIF"}</th>
                  <th>{t("tua_number")}</th>
                  <th></th>
                  <th className="text-end">{t("actions") || ""}</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted py-4">{t("no_data_found")}</td></tr>
                )}
                {list.map((s) => (
                  <tr key={s.id}>
                    <td><strong>{s.name}</strong></td>
                    <td>{s.nif || "—"}</td>
                    <td>{s.tua_number || "—"}</td>
                    <td>
                      {s.is_default === 1 && (
                        <span className="badge bg-primary">{t("default")}</span>
                      )}
                    </td>
                    <td className="text-end">
                      {s.is_default !== 1 && (
                        <button
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => setDefault(s)}
                          title={t("set_as_default")}
                        >
                          <i className="fa fa-star" />
                        </button>
                      )}
                      {RolePermission?.modify === 1 && (
                        <button
                          className="btn btn-sm btn-outline-secondary me-1"
                          onClick={() => openEdit(s)}
                        >
                          <i className="fa fa-pencil" />
                        </button>
                      )}
                      {RolePermission?.delete === 1 && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => { setDelTarget(s); setDelModal(true); }}
                        >
                          <i className="fa fa-trash" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {totalRecords > params.limit && (
          <div className="mt-3 d-flex justify-content-between">
            <select className="form-select w-auto" value={params.limit} onChange={(e) => handleParamChange(e, "length")}>
              {tableLengthList.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <ReactPaginate
              pageCount={Math.ceil(totalRecords / params.limit)}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              onPageChange={(e) => handleParamChange(e, "pagination")}
              containerClassName="pagination mb-0"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              activeClassName="active"
              forcePage={params.page - 1}
            />
          </div>
        )}

        <Modal isOpen={modifyModal} toggle={() => setModifyModal(false)} centered>
          <Form onSubmit={submit}>
            <ModalHeader toggle={() => setModifyModal(false)}>
              {form.id ? t("edit") : t("add_supplier")}
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>{t("name")}</Label>
                <input
                  className={`form-control ${errors.name ? "is-invalid" : ""}`}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </FormGroup>
              <div className="row">
                <div className="col-md-6">
                  <FormGroup>
                    <Label>NIF</Label>
                    <input
                      className="form-control"
                      value={form.nif}
                      onChange={(e) => setForm({ ...form, nif: e.target.value })}
                    />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label>{t("tua_number")}</Label>
                    <input
                      className="form-control"
                      value={form.tua_number}
                      onChange={(e) => setForm({ ...form, tua_number: e.target.value })}
                    />
                  </FormGroup>
                </div>
              </div>
              <FormGroup>
                <Label>{t("address")}</Label>
                <input
                  className="form-control"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>{t("notes")}</Label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </FormGroup>
              <FormGroup check>
                <Label check>
                  <input
                    type="checkbox"
                    checked={form.is_default === 1}
                    onChange={(e) =>
                      setForm({ ...form, is_default: e.target.checked ? 1 : 0 })
                    }
                  />{" "}
                  {t("set_as_default")}
                </Label>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setModifyModal(false)}>
                {t("cancel")}
              </Button>
              <Button color="primary" type="submit" disabled={isSaving}>
                {isSaving ? t("saving") : t("save")}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        <Modal isOpen={delModal} toggle={() => setDelModal(false)} centered>
          <ModalHeader toggle={() => setDelModal(false)}>{t("delete")}</ModalHeader>
          <ModalBody>{t("are_you_sure_selected_delete")}</ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setDelModal(false)}>{t("label_no")}</Button>
            <Button color="danger" onClick={confirmDelete} disabled={isDelLoad}>
              {isDelLoad ? "..." : t("label_yes")}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default SuppliersTP;
